import { isCanonicalEmail } from '@seller-hermes/utils/canonical-email';
import { LOGOUT_MARKER_COOKIE } from '@seller-hermes/utils/logout-marker';

const COGNITO_PREFIX = 'CognitoIdentityServiceProvider';
const COOKIE_DOMAIN = process.env['NX_COOKIE_DOMAIN'] ?? '.abbiamolog.com';
// Espelha `expires: 30` (dias) do cookieStorage configurado em app.tsx.
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

export type MigrationResult =
  | 'migrated'
  | 'skipped:no-window'
  | 'skipped:foreign-host'
  | 'skipped:logged-out'
  | 'skipped:no-client-id'
  | 'skipped:no-legacy-session'
  | 'skipped:cookie-session-already-present'
  | 'skipped:cookie-session-preserved';

interface StoredSession {
  username: string;
  entries: Array<[string, string]>;
  email: string | null;
  /** Epoch em ms do momento em que essa sessão foi autenticada. */
  authenticatedAt: number | null;
}

const canUseSharedCookieDomain = (): boolean =>
  typeof window !== 'undefined' &&
  window.location.hostname.endsWith(COOKIE_DOMAIN.replace(/^\./, ''));

// Espelha exatamente o encoding de NOME do js-cookie 2.2.1 (o que o Amplify v4
// usa por baixo do cookieStorage), em src/js.cookie.js:74-76. Detalhe que
// importa: `%40` NÃO está na lista de desescape do nome, então o `@` de um
// username com e-mail vira `%40`. Gravar o nome cru criaria um segundo cookie
// com o mesmo significado que o Amplify nunca conseguiria sobrescrever nem
// apagar — token órfão sobrevivendo ao logout.
const encodeCookieName = (name: string): string =>
  encodeURIComponent(name)
    .replace(/%(23|24|26|2B|5E|60|7C)/g, decodeURIComponent)
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');

const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const encodedName = encodeCookieName(name);
  const cookie = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${encodedName}=`));

  return cookie
    ? decodeURIComponent(cookie.slice(encodedName.length + 1))
    : null;
};

// O js-cookie (usado pelo Amplify v4 por baixo do cookieStorage) roda
// decodeURIComponent na leitura, então encodeURIComponent na escrita faz
// round-trip idêntico. O .replace() extra que o js-cookie aplica no set() só
// desescapa caracteres seguros por estética — decodeURIComponent aceita as duas
// formas.
//
// Domain e Path formam, junto com o nome, a identidade do cookie no browser.
// Divergir de app.tsx aqui não sobrescreve o cookie do Amplify: cria um
// segundo cookie de mesmo nome que o Amplify nunca consegue apagar (ele deleta
// especificando Domain=.abbiamolog.com), deixando um token órfão para trás.
const writeCookie = (name: string, value: string): void => {
  document.cookie = [
    `${encodeCookieName(name)}=${encodeURIComponent(value)}`,
    `Domain=${COOKIE_DOMAIN}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    'Secure',
  ].join('; ');
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const segment = token.split('.')[1];
  if (!segment) return null;

  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );

    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
};

// A claim `email` do idToken é a fonte da canonicidade — e não o LastAuthUser,
// que dependendo da configuração do user pool carrega o `sub` (UUID) em vez do
// e-mail.
const getEmailFromIdToken = (idToken?: string): string | null => {
  if (!idToken) return null;

  const payload = decodeJwtPayload(idToken);
  const email = payload?.['email'];

  return typeof email === 'string' ? email : null;
};

// `auth_time` é o momento da autenticação original e não muda em refresh —
// é exatamente o que queremos comparar contra o logout. `iat` é fallback.
const getAuthTimeFromIdToken = (idToken?: string): number | null => {
  if (!idToken) return null;

  const payload = decodeJwtPayload(idToken);
  const claim = payload?.['auth_time'] ?? payload?.['iat'];

  return typeof claim === 'number' ? claim * 1000 : null;
};

// publishLogoutMarker grava `${Date.now().toString(36)}-${random}`, então o
// primeiro segmento devolve o instante do logout.
const getLogoutMarkerTimestamp = (): number | null => {
  const marker = getCookieValue(LOGOUT_MARKER_COOKIE);
  if (!marker) return null;

  const parsed = Number.parseInt(marker.split('-')[0], 36);

  // Marker ilegível: trata como logout de instante desconhecido (bloqueia).
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const readLocalStorageSession = (clientId: string): StoredSession | null => {
  const username = window.localStorage.getItem(
    `${COGNITO_PREFIX}.${clientId}.LastAuthUser`,
  );
  if (!username) return null;

  // Copia todo o namespace do usuário em vez de enumerar sufixos conhecidos
  // (idToken/accessToken/refreshToken/clockDrift/userData/deviceKey/...): a
  // lista varia por versão do amazon-cognito-identity-js e por configuração de
  // device tracking. Copiar o prefixo inteiro é à prova de versão.
  const userPrefix = `${COGNITO_PREFIX}.${clientId}.${username}.`;
  const entries: Array<[string, string]> = [];

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key?.startsWith(userPrefix)) continue;

    const value = window.localStorage.getItem(key);
    if (value !== null) entries.push([key, value]);
  }

  if (entries.length === 0) return null;

  const idToken = entries.find(([key]) => key.endsWith('.idToken'))?.[1];

  return {
    username,
    entries,
    email: getEmailFromIdToken(idToken),
    authenticatedAt: getAuthTimeFromIdToken(idToken),
  };
};

const readCookieSession = (clientId: string): StoredSession | null => {
  const username = getCookieValue(`${COGNITO_PREFIX}.${clientId}.LastAuthUser`);
  if (!username) return null;

  const idToken = getCookieValue(
    `${COGNITO_PREFIX}.${clientId}.${username}.idToken`,
  );

  return {
    username,
    entries: [],
    email: getEmailFromIdToken(idToken),
    authenticatedAt: getAuthTimeFromIdToken(idToken),
  };
};

/**
 * Migra a sessão Cognito de localStorage para cookie no domínio compartilhado.
 *
 * Necessária porque o domínio legado (dashboard.abbiamolog.com) rodava com o
 * storage default do Amplify (localStorage) enquanto LOG/GO já usam
 * cookieStorage. Sem essa cópia, o primeiro load após a virada inicializa o
 * Amplify contra um cookieStorage vazio e todo cliente legado é deslogado.
 *
 * PRECISA rodar antes de `Amplify.configure`.
 */
export const migrateCognitoSessionToCookies = (): MigrationResult => {
  if (typeof window === 'undefined') return 'skipped:no-window';
  if (!canUseSharedCookieDomain()) return 'skipped:foreign-host';

  const clientId = process.env['NX_COGNITO_USER_POOL_WEB_CLIENT_ID'];
  if (!clientId) return 'skipped:no-client-id';

  const legacy = readLocalStorageSession(clientId);
  if (!legacy) return 'skipped:no-legacy-session';

  // O marker sinaliza que HOUVE um logout, não que o usuário está deslogado
  // AGORA — ele sobrevive 4h e ninguém o limpa no login. Comparar por presença
  // bloquearia todo mundo que deslogou do portal e depois entrou no dashboard
  // legado dentro dessa janela. O monitor em auth-session-expiry usa o mesmo
  // cookie mas dispara na MUDANÇA do valor; aqui, sem baseline anterior, a
  // equivalente correta é comparar instantes: só é ressurreição se a sessão do
  // localStorage for anterior ao logout.
  const logoutAt = getLogoutMarkerTimestamp();
  if (
    logoutAt !== null &&
    (legacy.authenticatedAt === null || legacy.authenticatedAt <= logoutAt)
  ) {
    return 'skipped:logged-out';
  }

  // Idempotência vem do ESTADO, não de um latch persistido. Um marker do tipo
  // "já migrei uma vez" seria de mão única: o cookie some por caminhos
  // normais — Auth.signOut() do portal, expiração de 30 dias, o usuário
  // limpando cookies — enquanto a sessão do localStorage continua viva (não a
  // apagamos, para permitir rollback). Com o latch, todo load seguinte viraria
  // no-op silencioso e o usuário ficaria permanentemente sem sessão. Perguntar
  // ao cookie se ele já tem sessão responde a mesma coisa sem essa armadilha.
  const existing = readCookieSession(clientId);
  if (existing) {
    if (existing.username === legacy.username) {
      return 'skipped:cookie-session-already-present';
    }

    // Canônico → preserva: o backfill já deu a esse login acesso aos seller
    // groups de todos os logins taggeados, é a sessão superior.
    //
    // Não classificável (sem idToken legível) → preserva também: destruir uma
    // sessão que não conseguimos identificar é pior do que deixar a sessão
    // legada de fora, já que o usuário segue para o portal de qualquer forma.
    if (existing.email === null || isCanonicalEmail(existing.email)) {
      return 'skipped:cookie-session-preserved';
    }
  }

  for (const [key, value] of legacy.entries) writeCookie(key, value);
  writeCookie(`${COGNITO_PREFIX}.${clientId}.LastAuthUser`, legacy.username);

  // O localStorage fica intacto de propósito: é o que permite rollback do
  // deploy reencontrar a sessão original. Rodar de novo num próximo load é
  // inofensivo — o guard de estado acima já corta, e a cópia é idempotente.
  return 'migrated';
};

/**
 * Remove o rastro do Cognito no localStorage.
 *
 * Só deve ser chamada em logout deliberado. Em falha transitória de refresh
 * (`tokenRefresh_failure`) NÃO deve rodar: apagaria a rede de segurança do
 * rollback de uma sessão que continua válida.
 *
 * Precisa rodar no próprio domínio legado — o portal não alcança o
 * localStorage de dashboard.abbiamolog.com (origens distintas), por isso o
 * gatilho é o logout marker compartilhado.
 */
export const purgeLegacyCognitoStorage = (): void => {
  if (typeof window === 'undefined') return;

  // Escopo pelo clientId desta aplicação. Prod e sandbox compartilham o
  // domínio de cookie `.abbiamolog.com` e, portanto, o abbiamo.logoutMarker —
  // que tem nome fixo nos dois ambientes. Purgar o prefixo Cognito inteiro faz
  // um logout em produção apagar a sessão de sandbox do mesmo browser (e
  // vice-versa). As chaves carregam o clientId, então dá para separar.
  const clientId = process.env['NX_COGNITO_USER_POOL_WEB_CLIENT_ID'];
  if (!clientId) return;

  const scopedPrefix = `${COGNITO_PREFIX}.${clientId}`;
  const keys: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(scopedPrefix)) keys.push(key);
  }

  keys.forEach((key) => window.localStorage.removeItem(key));
};
